import {
    createDataHook,
    createGetAction,
    createLoadingHook,
    createRestActions,
    createRestStore,
    RestStoreState,
} from '@intelliarts/ia-react-template';
import { createActionsHook, StoreActionApi } from 'react-sweet-state';

import { createMutationFailedCheck } from '../../../utils/restMutation';
import { ChangeReasonItem } from '../models/EmployeeDetails';
import { EmployeeStatus } from '../models/EmployeeStatus';

export enum ChangeReasonScopeType {
    STATUS = 'STATUS',
    EMPLOYMENT_PERIOD = 'EMPLOYMENT_PERIOD',
}

type ChangeReasonsState = RestStoreState<ChangeReasonItem>;

const actionConfig = {
    resourcePath: '/change-reasons',
};

const genericActions = createRestActions<ChangeReasonItem>(actionConfig);

export type ChangeReasonsScopeParams =
    | { scope: ChangeReasonScopeType.STATUS; status: EmployeeStatus }
    | { scope: ChangeReasonScopeType.EMPLOYMENT_PERIOD };

export const changeReasonsActions = {
    ...genericActions,
    loadByScope: (params: ChangeReasonsScopeParams) =>
        async ({ dispatch }: StoreActionApi<ChangeReasonsState>) => {
            const queryParams: Record<string, string | number | boolean> = { scope: params.scope };
            if (params.scope === ChangeReasonScopeType.STATUS) {
                queryParams.status = params.status;
            }
            const getAction = createGetAction<ChangeReasonItem>({ resourcePath: actionConfig.resourcePath });
            await dispatch(getAction({ params: queryParams }));
        },
    loadByStatus: (status: EmployeeStatus) =>
        async ({ dispatch }: StoreActionApi<ChangeReasonsState>) => {
            await dispatch(changeReasonsActions.loadByScope({
                scope: ChangeReasonScopeType.STATUS,
                status,
            }));
        },
    clearChangeReasons: () => ({ setState }: StoreActionApi<ChangeReasonsState>) => {
        setState({ data: [], isLoading: false, changes: [] });
    },
};

export const ChangeReasonsStore = createRestStore<ChangeReasonItem>({
    actions: changeReasonsActions,
    actionConfig,
    containerDisplayName: 'change-reasons-container',
    onInitAction: null,
});

export const StatusHistoryDialogChangeReasonsStore = createRestStore<ChangeReasonItem>({
    actions: changeReasonsActions,
    actionConfig,
    containerDisplayName: 'status-history-dialog-change-reasons-container',
    onInitAction: null,
});

export const useChangeReasonsData = createDataHook(ChangeReasonsStore);
export const useChangeReasonsIsLoading = createLoadingHook(ChangeReasonsStore);
export const useChangeReasonsActions = createActionsHook(ChangeReasonsStore);
export const didChangeReasonsMutationFail = createMutationFailedCheck(actionConfig.resourcePath);
export const useStatusHistoryDialogChangeReasonsData = createDataHook(StatusHistoryDialogChangeReasonsStore);
export const useStatusHistoryDialogChangeReasonsIsLoading = createLoadingHook(StatusHistoryDialogChangeReasonsStore);
export const useStatusHistoryDialogChangeReasonsActions = createActionsHook(StatusHistoryDialogChangeReasonsStore);
